import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "@evertrue/et-components";
import { updateVideo } from "../../entities/helpers/thankview-helper";
import { useModal } from "./hooks/modal-context";
import PropTypes from "prop-types";
import { useRecorder } from "./hooks/recorder-context";

const AccordionItem = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="settings--accordion-item">
      <div className="settings--accordion-header" onClick={() => setIsOpen(!isOpen)}>
        {title}
        <i className={`fa fa-chevron-${isOpen ? "up" : "down"}`}></i>
      </div>
      {isOpen && <div className="settings--accordion-content">{children}</div>}
    </div>
  );
};

const ToggleSwitch = ({ label, checked, onChange }) => (
  <div className="settings--setting">
    <span>{label}</span>
    <label className="settings--toggle-switch">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="settings--slider"></span>
    </label>
  </div>
);

const ThankviewSettings = ({ selectedVideo }) => {
  const { clientId } = useRecorder();
  const { openDefaultSettings } = useModal();
  const [settings, setSettings] = useState({
    logo_enabled: selectedVideo ? selectedVideo.data.personalVideo.logo_enabled : true,
    button_enabled: selectedVideo ? selectedVideo.data.personalVideo.button_enabled : true,
    button_text: selectedVideo ? selectedVideo.data.personalVideo.button_text : "",
    button_url: selectedVideo ? selectedVideo.data.personalVideo.button_url : "",
    show_in_email: selectedVideo ? selectedVideo.data.personalVideo.show_in_email : true,
    thumbnail_type: selectedVideo ? selectedVideo.data.personalVideo.thumbnail_type : true,
  });

  const initialSettings = useRef(settings);
  const updateTimerRef = useRef(null);

  const handleToggle = useCallback((setting) => {
    setSettings((prev) => ({ ...prev, [setting]: !prev[setting] }));
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: value,
    }));
  }, []);

  const debouncedUpdate = useCallback(() => {
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }

    updateTimerRef.current = setTimeout(() => {
      const changedSettings = Object.entries(settings).reduce((acc, [key, value]) => {
        if (value !== initialSettings.current[key]) {
          acc[key] = value;
        }
        return acc;
      }, {});

      if (Object.keys(changedSettings).length > 0) {
        const formattedChanges = {
          personalVideo: changedSettings,
        };
        updateVideo(
          selectedVideo.data.personalVideo.id,
          formattedChanges,
          clientId.data.client_id,
          clientId.data.client_secret
        )
          .then(() => {
            initialSettings.current = { ...settings };
            console.log("Video settings updated successfully");
          })
          .catch((error) => {
            console.error("Failed to update video settings:", error);
          });
      }
    }, 1000); // 1 second debounce
  }, [settings, selectedVideo.data.personalVideo.id, clientId]);

  useEffect(() => {
    debouncedUpdate();

    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, [debouncedUpdate]);
  return (
    <div className="settings--accordion-body">
      <div className="settings--accordion-container">
        <div>
          <AccordionItem title="Landing Page Settings">
            <ToggleSwitch
              label="Show Logo on Landing Page"
              checked={settings.logo_enabled}
              onChange={() => handleToggle("logo_enabled")}
            />
          </AccordionItem>

          <AccordionItem title="Call-To-Action Button Settings">
            <ToggleSwitch
              label="Toggle Call-To-Action Button"
              checked={settings.button_enabled}
              onChange={() => handleToggle("button_enabled")}
            />
            <input
              type="text"
              id="button_text"
              name="button_text"
              value={settings.button_text || ""}
              onChange={handleInputChange}
            />
            <input
              type="text"
              id="button_url"
              name="button_url"
              value={settings.button_url || ""}
              onChange={handleInputChange}
            />
            <p className="settings--info">Ensure URL contains https://</p>
            <ToggleSwitch
              label="Show Button in Email"
              checked={settings.show_in_email}
              onChange={() => handleToggle("show_in_email")}
            />
            {/* <ToggleSwitch
              label="Show Button on Landing Page"
              checked={settings.showButtonOnLanding}
              onChange={() => handleToggle("showButtonOnLanding")}
            /> */}
          </AccordionItem>

          <AccordionItem title="Email Settings">
            <ToggleSwitch
              label="Animated Thumbnail"
              checked={settings.thumbnail_type}
              onChange={() => handleToggle("thumbnail_type")}
            />
          </AccordionItem>

          {/* <AccordionItem title="Video Settings">
            <ToggleSwitch
              label="Enable Closed Captions on Video"
              checked={settings.enableClosedCaptions}
              onChange={() => handleToggle("enableClosedCaptions")}
            />
          </AccordionItem> */}
        </div>
        <div className="settings--change-defaults">
          <Link onClick={openDefaultSettings}>Change Defaults</Link>
        </div>
      </div>
    </div>
  );
};

AccordionItem.propTypes = {
  title: PropTypes.string,
  children: PropTypes.any,
};

ToggleSwitch.propTypes = {
  label: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
};
ThankviewSettings.propTypes = {
  selectedVideo: PropTypes.object,
};

export default ThankviewSettings;
